import { stringify } from "qs";

/**
 * Client wrapper for the server-side HERE relay (MapsController / HereApi).
 *
 * Replaces the former browser-side google.maps.places.Autocomplete and
 * google.maps.Geocoder. No API key in the browser — every call goes through
 * /maps/* endpoints so it works on any custom domain.
 *
 * Locations are returned in the app's snake_case address fields:
 *   place_id, formatted_address, street_number, route, locality,
 *   administrative_area, administrative_area_code, postal_code,
 *   country, country_code, latitude, longitude
 */
async function get(path, params) {
  const qs = params ? `?${stringify(params)}` : "";
  const res = await fetch(path + qs, { credentials: "include" });
  if (!res.ok) return null;
  return res.json();
}

class GeoService {
  /**
   * Address suggestions for a partial query (min 3 chars server-side).
   * @param {string} query
   * @param {{at?: {lat:number, lng:number}, lang?: string, in?: string, countries?: string[]}} [opts]
   * @returns {Promise<Array<{id:string, label:string}>>}
   */
  static async autocomplete(query, opts = {}) {
    const params = { q: query };
    if (opts.at && opts.at.lat != null && opts.at.lng != null) {
      params.lat = opts.at.lat;
      params.lng = opts.at.lng;
    }
    if (opts.lang) params.lang = opts.lang;
    if (opts.in) params.in = opts.in;
    if (opts.countries && opts.countries.length) params.countries = opts.countries.join(",");
    const res = await get("/maps/autocomplete", params);
    return res && res.status === "success" && Array.isArray(res.suggestions) ? res.suggestions : [];
  }

  /**
   * Resolve an autocomplete suggestion id to a full normalized location.
   * @param {string} id
   * @param {{lang?: string, unit?: string}} [opts]
   * @returns {Promise<object|null>}
   */
  static async details(id, opts = {}) {
    const params = { id };
    if (opts.lang) params.lang = opts.lang;
    if (opts.unit) params.unit = opts.unit;
    const res = await get("/maps/details", params);
    return res && res.status === "success" ? res.location || null : null;
  }

  /**
   * Reverse geocode coordinates to a location.
   * @param {number} lat
   * @param {number} lng
   * @param {{lang?: string}} [opts]
   * @returns {Promise<object|null>}
   */
  static async reverse(lat, lng, opts = {}) {
    const params = { lat, lng };
    if (opts.lang) params.lang = opts.lang;
    const res = await get("/maps/reverse", params);
    return res && res.status === "success" ? res.location || null : null;
  }

  /**
   * Forward geocode a free-text address to a single location.
   * @param {string} address
   * @param {{lang?: string, in?: string}} [opts]
   * @returns {Promise<object|null>}
   */
  static async geocode(address, opts = {}) {
    const suggestions = await GeoService.autocomplete(address, opts);
    if (!suggestions.length) return null;
    return GeoService.details(suggestions[0].id, opts);
  }
}

export default GeoService;
